import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { OwlOptions, CarouselComponent } from "ngx-owl-carousel-o";
import { AnalyticsService } from "src/app/shared/services/analytics.service";

interface Project {
    image: string;
    title: string;
    category: string;
    slug: string;
    link?: string;
}

@Component({
    selector: "app-home",
    templateUrl: "./home-one.component.html",
    styleUrls: ["./home-one.component.scss"],
})
export class HomeOneComponent implements OnInit {
    @ViewChild(CarouselComponent) carousel: CarouselComponent;
    modalActive: boolean = false;
    selectedImage: string = "";
    lastScrollPosition: number = 0;

    // Carousel Config
    startPosition: number = 0;

    customOptions: OwlOptions = {
        loop: true,
        mouseDrag: true,
        touchDrag: true,
        pullDrag: false,
        dots: true,
        navSpeed: 700,
        navText: [
            '<i class="bx bx-chevron-left"></i>',
            '<i class="bx bx-chevron-right"></i>',
        ],
        responsive: {
            0: { items: 1 },
            400: { items: 1 },
            740: { items: 1 },
            940: { items: 1 },
        },
        nav: true,
        startPosition: 0,
    };

    projects: Project[] = [
        {
            image: "assets/img/projects/manda-ravalison-portfolio.jpg",
            title: "This Portfolio",
            category: "Angular & Modern UI/UX",
            slug: "angular-mandarvl-portfolio",
            link: "https://github.com/mandarvl",
        },
        {
            image: "assets/img/projects/pms-online-booking.jpg",
            title: "PMS with Hotel Booking & Payment",
            slug: "pms-online-booking-hotel",
            category: "Property Management System",
        },
        {
            image: "assets/img/projects/hr-agency-website.jpg",
            title: "HR Agency Website",
            slug: "hr-agency-website",
            category: "Online Application System",
        },
        {
            image: "assets/img/projects/e-commerce-platform.jpg",
            title: "E-commerce Platform",
            slug: "e-commerce-platform",
            category: "Full-stack Online Store",
        },
        {
            image: "assets/img/projects/e-rental-platform.jpg",
            title: "E-rental Platform",
            slug: "e-rental-platform",
            category: "Rental Management Solution",
        },
        {
            image: "assets/img/projects/customer-relationship-management-system.jpg",
            title: "CRM System",
            slug: "customer-relationship-management-system",
            category: "Customer Relationship Management System",
        },
    ];

    constructor(
        public analyticsService: AnalyticsService,
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
    ) {}

    ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            this.handleModalState(params["project"]);
        });

        // Listen for back/forward button clicks
        this.location.subscribe(() => {
            const params = new URLSearchParams(window.location.search);
            this.handleModalState(params.get("project"));
        });
    }

    private handleModalState(projectIndexOrSlug: string | null) {
        if (projectIndexOrSlug) {
            let index = parseInt(projectIndexOrSlug, 10);
            if (isNaN(index)) {
                index = this.projects.findIndex(
                    (p) => p.slug === projectIndexOrSlug,
                );
            }

            if (index !== -1 && this.projects[index]) {
                this.startPosition = index;
                this.modalActive = true;
                // Update options to force start position
                this.customOptions = {
                    ...this.customOptions,
                    startPosition: index,
                };
                document.body.style.overflow = "hidden";
            } else {
                // Invalid project, close modal
                this.closeModal();
            }
        } else {
            this.modalActive = false;
            document.body.style.overflow = "auto";

            if (this.lastScrollPosition > 0) {
                setTimeout(() => {
                    window.scrollTo(0, this.lastScrollPosition);
                }, 0);
            }
        }
    }

    openModal(index: number) {
        this.analyticsService.suppressNextPageView = true;
        this.lastScrollPosition = window.scrollY;

        if (this.location.path(true).indexOf("#") !== -1) {
            this.location.replaceState(this.location.path(false));
        }

        const project = this.projects[index];
        const url = this.router
            .createUrlTree([], {
                relativeTo: this.route,
                queryParams: { project: project.slug },
                queryParamsHandling: "merge",
                preserveFragment: false,
            })
            .toString();

        this.location.go(url);
        this.startPosition = index;
        this.modalActive = true;

        this.customOptions = { ...this.customOptions, startPosition: index };
        document.body.style.overflow = "hidden";

        this.onProjectClick(project.title, project.slug);
    }

    closeModal() {
        this.analyticsService.suppressNextPageView = true;
        const url = this.router
            .createUrlTree([], {
                relativeTo: this.route,
                queryParams: { project: null },
                queryParamsHandling: "merge",
                preserveFragment: false,
            })
            .toString();

        this.location.go(url);
        this.modalActive = false;
        document.body.style.overflow = "auto";
        if (this.lastScrollPosition > 0) {
            setTimeout(() => {
                window.scrollTo(0, this.lastScrollPosition);
            }, 0);
        }
    }

    onProjectClick(title: string, slug: string) {
        this.analyticsService.trackEvent("project_click", {
            title,
            slug,
        });
    }

    onEmailClick(section: string) {
        this.analyticsService.trackEvent("email_click", {
            section,
        });
    }

    onSocialClick(platform: string) {
        this.analyticsService.trackEvent("social_click", {
            platform,
        });
    }
}
